import { Fragment, useContext } from 'react'
import { Menu, Dialog, Transition } from '@headlessui/react'
import { AuthContext } from "../utils/auth.js";
import { useDevSidebar } from '../context/DevSidebarContext.js';
import {
    Bars3Icon,
    FolderIcon,
    HomeIcon,
    XMarkIcon,
    UserCircleIcon,
    CodeBracketIcon,
    CreditCardIcon,
    ShieldCheckIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline'

import { FolderSearch } from "lucide-react";

import app from "../utils/firebaseApp.js";
import { getAuth } from "firebase/auth";
import { isAdminEmail } from "../utils/adminUsers.js";
import DevDarkModeToggle from "./DevDarkModeToggle.js";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function MenuBar({
    sidebarOpen,
    setSidebarOpen,
    currentHref,
}) {
    const { currentUser } = useContext(AuthContext);
    const { collapsed, toggleCollapsed } = useDevSidebar();
    const isCollapsed = collapsed;

    let navigation = [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: currentHref === "dashboard" },
        { name: 'Data Extraction', href: '/extractions', icon: FolderIcon, current: currentHref === "extractions" },
        { name: 'Document Classification', href: '/classifications', icon: FolderSearch, current: currentHref === "classifications" },
        { name: 'Account', href: '/account', icon: UserCircleIcon, current: currentHref === "account" },
        { name: 'Billing', href: '/billing', icon: CreditCardIcon, current: currentHref === "billing", show: true },
        { name: 'API', href: '/api', icon: CodeBracketIcon, current: currentHref === "api" },
    ];

    const canAccessAdmin = isAdminEmail(currentUser?.email);

    const currentNavigation = navigation.filter((item) => item.current)[0];

    // remove all navigation with show false
    navigation = navigation.filter((item) => item.show !== false);

    const userNavigation = [
        { name: 'Your profile', href: '/account' },
    ]

    const handleSignout = () => {
        getAuth(app).signOut(app).then(() => {
            // console.log("Signout")
        }).catch((error) => {
            // console.log(error)
        });
    }

    return (
        <>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                        <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>
                                <div className="app-sidebar flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-600 px-6 pb-2">
                                    <div className="mt-2 flex h-16 shrink-0 items-center">
                                        <img
                                            className="h-8 w-auto"
                                            src={process.env.PUBLIC_URL + '/logo_extracta_labs_white.png'}
                                            alt="Extracta LABS"
                                        />
                                    </div>
                                    <nav className="flex flex-1 flex-col">
                                        <ul className="flex flex-1 flex-col gap-y-7">
                                            <li>
                                                <ul className="-mx-2 space-y-1">
                                                    {navigation.map((item) => (
                                                        <li key={item.name}>
                                                            <a
                                                                href={item.href}
                                                                className={classNames(
                                                                    item.current
                                                                        ? 'bg-indigo-700 text-white'
                                                                        : 'text-indigo-200 hover:text-white hover:bg-indigo-700',
                                                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold relative'
                                                                )}
                                                            >
                                                                <item.icon
                                                                    className={classNames(
                                                                        item.current ? 'text-white' : 'text-indigo-200 group-hover:text-white',
                                                                        'h-6 w-6 shrink-0'
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                                {item.name}
                                                                {item.tag && (
                                                                    <span className="app-sidebar-tag absolute top-1/2 -translate-y-1/2 right-0 translate-x-[15%] inline-flex items-center rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 px-2.5 py-[0.5px] text-[11px] font-medium text-white shadow-sm ring-1 ring-inset ring-white/20 transition-all duration-200 hover:shadow-indigo-200/50">
                                                                        {item.tag}
                                                                    </span>
                                                                )}
                                                            </a>
                                                            {item.children && item.current && (
                                                                <ul className="mt-1 ml-10 space-y-1">
                                                                    {item.children.map((child) => (
                                                                        <li key={child.name}>
                                                                            <a
                                                                                href={child.href}
                                                                                className={classNames(
                                                                                    child.current
                                                                                        ? 'text-white'
                                                                                        : 'text-indigo-200 hover:text-white',
                                                                                    'block text-sm leading-6 font-normal'
                                                                                )}
                                                                            >
                                                                                {child.name}
                                                                            </a>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                        </ul>
                                    </nav>
                                    {canAccessAdmin && (
                                        <div className="pb-4">
                                            <a
                                                href="/admin"
                                                className="group flex gap-x-3 rounded-md bg-white/10 p-2 text-sm leading-6 font-semibold text-white ring-1 ring-inset ring-white/15 hover:bg-white/15"
                                            >
                                                <ShieldCheckIcon className="h-6 w-6 shrink-0 text-indigo-100" aria-hidden="true" />
                                                Admin console
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div className={classNames(
                'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ease-in-out',
                isCollapsed ? 'lg:w-16' : 'lg:w-72',
            )}>
                <div className={classNames(
                    'app-sidebar flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-600',
                    isCollapsed ? 'px-2' : 'px-6',
                )}>
                    <div className={classNames(
                        'mt-2 flex h-16 shrink-0 items-center',
                        isCollapsed ? 'justify-center' : '',
                    )}>
                        {isCollapsed ? (
                            <span className="app-sidebar-collapsed-logo flex h-9 w-9 items-center justify-center rounded-lg bg-white shadow-sm">
                                <img
                                    className="h-6 w-6 object-contain"
                                    src={process.env.PUBLIC_URL + '/logo192.png'}
                                    alt="Extracta LABS"
                                />
                            </span>
                        ) : (
                            <img
                                className="h-8 w-auto"
                                src={process.env.PUBLIC_URL + '/logo_extracta_labs_white.png'}
                                alt="Extracta LABS"
                            />
                        )}
                    </div>
                    <nav className="flex flex-1 flex-col">
                        <ul className="flex flex-1 flex-col gap-y-7">
                            <li>
                                <ul className={classNames(isCollapsed ? 'space-y-1' : '-mx-2 space-y-1')}>
                                    {navigation.map((item) => (
                                        <li key={item.name}>
                                            <a
                                                href={item.href}
                                                title={isCollapsed ? item.name : undefined}
                                                className={classNames(
                                                    item.current
                                                        ? 'bg-indigo-700 text-white'
                                                        : 'text-indigo-200 hover:text-white hover:bg-indigo-700',
                                                    'group flex rounded-md p-2 text-sm leading-6 font-semibold relative',
                                                    isCollapsed ? 'justify-center' : 'gap-x-3',
                                                )}
                                            >
                                                <item.icon
                                                    className={classNames(
                                                        item.current ? 'text-white' : 'text-indigo-200 group-hover:text-white',
                                                        'h-6 w-6 shrink-0'
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {!isCollapsed && item.name}
                                                {!isCollapsed && item.tag && (
                                                    <span className="app-sidebar-tag absolute top-1/2 -translate-y-1/2 right-0 translate-x-[15%] inline-flex items-center rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 px-2.5 py-[0.5px] text-[11px] font-medium text-white shadow-sm ring-1 ring-inset ring-white/20 transition-all duration-200 hover:shadow-indigo-200/50">
                                                        {item.tag}
                                                    </span>
                                                )}
                                                {isCollapsed && item.tag && (
                                                    <span className="app-sidebar-tag-dot absolute top-1 right-1 h-2 w-2 rounded-full bg-indigo-400" aria-hidden="true" />
                                                )}
                                            </a>
                                            {!isCollapsed && item.children && item.current && (
                                                <ul className="mt-1 ml-10 space-y-1">
                                                    {item.children.map((child) => (
                                                        <li key={child.name}>
                                                            <a
                                                                href={child.href}
                                                                className={classNames(
                                                                    child.current
                                                                        ? 'text-white'
                                                                        : 'text-indigo-200 hover:text-white',
                                                                    'block text-sm leading-6 font-normal'
                                                                )}
                                                            >
                                                                {child.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        </ul>
                    </nav>
                    {canAccessAdmin && (
                        <div className={classNames('pb-3', isCollapsed ? 'flex justify-center' : '')}>
                            <a
                                href="/admin"
                                title={isCollapsed ? 'Admin console' : undefined}
                                className={classNames(
                                    'group flex rounded-md bg-white/10 p-2 text-sm leading-6 font-semibold text-white ring-1 ring-inset ring-white/15 hover:bg-white/15',
                                    isCollapsed ? 'justify-center' : 'gap-x-3',
                                )}
                            >
                                <ShieldCheckIcon className="h-6 w-6 shrink-0 text-indigo-100" aria-hidden="true" />
                                {!isCollapsed && 'Admin console'}
                            </a>
                        </div>
                    )}
                    <div className={classNames('pb-4', isCollapsed ? 'flex justify-center' : '')}>
                        <button
                            type="button"
                            onClick={toggleCollapsed}
                            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            className={classNames(
                                'flex items-center rounded-md p-2 text-indigo-200 hover:bg-indigo-700 hover:text-white transition-colors',
                                isCollapsed ? 'justify-center' : 'gap-x-2 w-full',
                            )}
                        >
                            {isCollapsed ? (
                                <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                                <>
                                    <ChevronDoubleLeftIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                                    <span className="text-sm font-medium">Collapse</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="app-sidebar sticky top-0 z-40 flex flex-row items-center gap-x-6 bg-indigo-600 px-4 py-4 shadow-sm sm:px-6 lg:hidden ">
                <button type="button" className="-m-2.5 p-2.5 text-indigo-200 lg:hidden" onClick={() => setSidebarOpen(true)}>
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
                <div className="w-full text-sm font-semibold leading-6 text-white">{currentNavigation.name}</div>
                <div className='flex flex-grow items-center justify-end gap-2'>
                    <DevDarkModeToggle className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-2.5 text-xs font-semibold leading-none text-white ring-1 ring-white/20 hover:bg-white/15" />
                    <span className="sr-only">Your profile</span>

                    <Menu as="div" className="relative flex-none">
                        <Menu.Button className="-m-2.5 block p-2.5 text-white hover:text-white">
                            <span className="sr-only">Open options</span>
                            <span className="inline-flex items-center justify-center mt-1.5 h-6 w-6 overflow-hidden rounded-full bg-gray-100">
                                <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24" style={{ margin: 0, padding: 0 }}>
                                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </span>
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute right-0 top-full z-50 mt-2 w-48 divide-y divide-gray-100 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                                <div className="px-4 py-3">
                                    <p className="text-sm">Signed in as</p>
                                    <p className="truncate text-sm font-medium text-gray-900">{currentUser.email}</p>
                                </div>
                                <div className="py-1">
                                    {userNavigation.map((item) => (
                                        <Menu.Item key={item.name}>
                                            {({ active }) => (
                                                <a
                                                    href={item.href}
                                                    className={classNames(
                                                        active ? 'bg-gray-50' : '',
                                                        'block px-3 py-1 text-sm leading-6 text-gray-900'
                                                    )}
                                                >
                                                    {item.name}
                                                </a>
                                            )}
                                        </Menu.Item>
                                    ))}
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={handleSignout}
                                                className={classNames(
                                                    active ? 'bg-gray-50' : '',
                                                    'w-full text-start px-3 py-1 text-sm leading-6 text-gray-900'
                                                )}
                                            >
                                                Log out<span className="sr-only">, </span>
                                            </button>
                                        )}
                                    </Menu.Item>
                                    {canAccessAdmin && (
                                        <Menu.Item>
                                            {({ active }) => (
                                                <a
                                                    href="/admin"
                                                    className={classNames(
                                                        active ? 'bg-gray-50' : '',
                                                        'block px-3 py-1 text-sm leading-6 text-gray-900'
                                                    )}
                                                >
                                                    Admin console
                                                </a>
                                            )}
                                        </Menu.Item>
                                    )}
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>

        </>
    )
}
